import {
    IFieldPropertyMinimal,
    IJobTaskRunDetail,
    JobTaskStatus,
    JobTaskType,
} from "@/api/admin/admin.types";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Stack,
    Typography,
} from "@mui/material";
import {
    CheckCircle2,
    ChevronDown,
    Circle,
    CircleAlert,
    ExternalLink,
    RotateCcw,
} from "lucide-react";
import { useState } from "react";
import {
    getTaskDuration,
    getTaskMetadataLabel,
    TASK_TYPE_LABELS,
} from "../utils";
import { LiveDuration } from "./LiveDuration";

const STATUS_CONFIG: Record<
    JobTaskStatus,
    { icon: React.ElementType; color: string }
> = {
    [JobTaskStatus.COMPLETED]: { icon: CheckCircle2, color: "#86efac" },
    [JobTaskStatus.PROCESSING]: { icon: Circle, color: "#d97706" },
    [JobTaskStatus.QUEUED]: { icon: Circle, color: "#9ca3af" },
    [JobTaskStatus.FAILED]: { icon: CircleAlert, color: "#dc2626" },
    [JobTaskStatus.RETRYABLE]: { icon: RotateCcw, color: "#d97706" },
    [JobTaskStatus.CANCELLED]: { icon: CircleAlert, color: "#6b7280" },
};

const PropertyList = ({
    properties,
}: {
    properties: IFieldPropertyMinimal[];
}) =>
    properties.length > 0 ? (
        <Stack spacing={0.25}>
            {properties.map((prop) => (
                <Stack
                    key={prop.name}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >
                    <Typography
                        variant="caption"
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                        {prop.name}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: "0.75rem",
                            color: "#9ca3af",
                            fontFamily: "monospace",
                        }}
                    >
                        {prop.datatype}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    ) : null;

interface TaskRowProps {
    task: IJobTaskRunDetail;
    hideTaskType?: boolean;
}

export const TaskRow = ({ task, hideTaskType = false }: TaskRowProps) => {
    const hasFailed =
        task.status === JobTaskStatus.FAILED ||
        task.status === JobTaskStatus.CANCELLED;
    const hasError = hasFailed && task.error_message;

    const meta = task.metadata_;
    const hasClauses =
        task.task_type === JobTaskType.CLAUSE_ANALYSIS_BATCH &&
        meta?.clauses &&
        meta.clauses.length > 0;
    const hasFieldDef =
        task.task_type === JobTaskType.FIELD_EXTRACTION &&
        meta?.field_definition;
    const hasFieldDefs =
        task.task_type === JobTaskType.FIELD_EXTRACTION &&
        meta?.field_definitions &&
        meta.field_definitions.length > 0;

    const isExpandable = !!(
        hasError ||
        hasClauses ||
        hasFieldDef ||
        hasFieldDefs
    );
    const [expanded, setExpanded] = useState(hasFailed);

    const config = STATUS_CONFIG[task.status];
    const Icon = config.icon;
    const duration = getTaskDuration(task);
    const isLive =
        (task.status === JobTaskStatus.PROCESSING ||
            task.status === JobTaskStatus.RETRYABLE) &&
        !!task.processing_at;
    const metadataLabel = getTaskMetadataLabel(task);
    const retryExhausted = task.retry_count >= task.max_retries;

    return (
        <Box
            sx={{
                "&:not(:last-child)": {
                    borderBottom: "1px solid",
                    borderColor: "#f1f5f9",
                },
                "&:hover": {
                    backgroundColor: "#f8fafc",
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    py: 1.25,
                    px: 2.5,
                    cursor: isExpandable ? "pointer" : undefined,
                }}
                onClick={
                    isExpandable ? () => setExpanded(!expanded) : undefined
                }
            >
                {task.status === JobTaskStatus.PROCESSING ? (
                    <CircularProgress size={16} sx={{ color: config.color }} />
                ) : (
                    <Icon size={16} color={config.color} />
                )}

                {!hideTaskType && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {TASK_TYPE_LABELS[task.task_type]}
                    </Typography>
                )}

                {metadataLabel &&
                task.task_type === JobTaskType.TOPIC_SUMMARY &&
                meta?.topic_id ? (
                    <LinkRouter
                        to={`/admin/topics/${meta.topic_id}/details`}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.875rem",
                            color: "#233876",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": {
                                textDecoration: "underline",
                            },
                        }}
                    >
                        {metadataLabel}
                        <ExternalLink size={11} color="#9ca3af" />
                    </LinkRouter>
                ) : metadataLabel ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {metadataLabel}
                    </Typography>
                ) : (
                    <Box sx={{ flex: 1 }} />
                )}

                {task.retry_count > 0 && (
                    <Chip
                        label={`${task.retry_count}/${task.max_retries} retries`}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            borderRadius: "4px",
                            backgroundColor: retryExhausted
                                ? "#fef2f2"
                                : "#fef3c7",
                            color: retryExhausted ? "#dc2626" : "#92400e",
                        }}
                    />
                )}

                {(isLive || duration) && (
                    <Box
                        sx={{
                            minWidth: 60,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                        }}
                    >
                        {isLive ? (
                            <LiveDuration
                                startTime={task.processing_at!}
                                variant="body2"
                                sx={{ fontSize: "inherit", color: "inherit" }}
                            />
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{ fontSize: "inherit", color: "inherit" }}
                            >
                                {duration}
                            </Typography>
                        )}
                    </Box>
                )}

                {isExpandable && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(!expanded);
                        }}
                        sx={{
                            display: "flex",
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                            transform: expanded
                                ? "rotate(0deg)"
                                : "rotate(-90deg)",
                        }}
                    >
                        <ChevronDown size={16} />
                    </Box>
                )}
            </Stack>

            {hasError && (
                <Collapse in={expanded}>
                    <Box
                        sx={{
                            mx: 2.5,
                            mb: 1,
                            p: 1.5,
                            backgroundColor: "#fef2f2",
                            borderRadius: "8px",
                            border: "1px solid #fee2e2",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: "#991b1b",
                            }}
                        >
                            {task.error_message}
                        </Typography>
                    </Box>
                </Collapse>
            )}

            {hasClauses && (
                <Collapse in={expanded}>
                    <Box sx={{ mx: 2.5, mb: 1 }}>
                        {meta!.clauses!.map((clause, idx) => (
                            <LinkRouter
                                key={`${clause.id}-${idx}`}
                                to={`/admin/clauses/${clause.id}/details`}
                                target="_blank"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    py: 0.5,
                                    px: 1.5,
                                    borderRadius: 0.5,
                                    fontSize: "0.75rem",
                                    color: "#233876",
                                    textDecoration: "none",
                                    "&:hover": {
                                        backgroundColor: "#f1f5f9",
                                        textDecoration: "none",
                                    },
                                }}
                            >
                                {clause.title}
                                <ExternalLink size={11} color="#9ca3af" />
                                {clause.section_type_names?.map((name) => (
                                    <Chip
                                        key={name}
                                        label={name}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: "0.7rem",
                                            borderRadius: "4px",
                                            backgroundColor: "#f1f5f9",
                                            color: "#64748b",
                                            ml: 0.25,
                                        }}
                                    />
                                ))}
                            </LinkRouter>
                        ))}
                    </Box>
                </Collapse>
            )}

            {hasFieldDef && (
                <Collapse in={expanded}>
                    <Box sx={{ mx: 2.5, mb: 1, px: 1.5, py: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                mb: 0.5,
                            }}
                        >
                            {meta!.field_definition!.name}
                        </Typography>
                        <PropertyList
                            properties={meta!.field_definition!.properties}
                        />
                    </Box>
                </Collapse>
            )}

            {hasFieldDefs && (
                <Collapse in={expanded}>
                    <Box sx={{ mx: 2.5, mb: 1 }}>
                        {meta!.field_definitions!.map((fd) => (
                            <Box key={fd.id} sx={{ mb: 0.75 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        py: 0.5,
                                        px: 1.5,
                                    }}
                                >
                                    {fd.name}
                                </Typography>
                                <Box sx={{ pl: 3 }}>
                                    <PropertyList properties={fd.properties} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};
